import dayjs from "dayjs";

const parseJsonField = (value: unknown): Record<string, any> => {
  if (!value) return {};
  if (typeof value === "object") return value as Record<string, any>;
  try {
    return JSON.parse(String(value));
  } catch {
    return {};
  }
};

const formatDateField = (value: unknown, dateFormat: string) =>
  value ? dayjs(value as dayjs.ConfigType).format(dateFormat) : null;

const splitTildeField = (value: unknown) => {
  const parts = String(value ?? "").split("~");
  return { code: parts[0] || "", name: parts[1] || parts[0] || "" };
};

/** Form `items` (procedure rows) → payload `treatmentPlansData`. */
export const mapFormItemsToTreatmentPlansData = (
  items: any[] = [],
  dateFormat = "DD-MM-YYYY HH:mm:ss",
): any[] => {
  return (items || []).map((item) => {
    const procParsed = parseJsonField(item?.procedureCode);
    const stratParsed = parseJsonField(item?.stratOptionValue);

    const procedureCode =
      procParsed.procedureCode ??
      item?.externalProcedureCode ??
      procParsed.id;

    const stratification =
      item?.stratOptionValue && Object.keys(stratParsed).length
        ? {
            id:
              stratParsed.stratificationCode ??
              stratParsed.stratificationid ??
              stratParsed.id ??
              null,
            name:
              stratParsed.stratificationName ?? stratParsed.name ?? null,
          }
        : {};

    const referral = splitTildeField(item?.procedureReferral);
    const accreditation = splitTildeField(item?.accreditationStatus);
    const treatingDoctor = splitTildeField(item?.treatingDoctor);

    return {
      speciality: item?.speciality || {},
      procedure: {
        id: procedureCode,
        name: procParsed.procedureName ?? item?.procedureName ?? "",
        procedureName: item?.unspecifiedProcedureName ?? null,
        procedureAmount:
          item?.unspecifiedProcedureAmount ?? item?.procedureAmount ?? null,
      },
      stratification,
      numberOfDaysPerUnits:
        item?.numberOfDays != null ? Number(item.numberOfDays) : null,
      treatmentStartDate: formatDateField(item?.treatmentStartDate, dateFormat),
      externalProcedureCode: item?.externalProcedureCode ?? procedureCode,
      procedureAmount: item?.procedureAmount,
      unspecifiedProcedureName: item?.unspecifiedProcedureName,
      unspecifiedProcedureAmount: item?.unspecifiedProcedureAmount,
      isAmountAutoFilled: item?.isAmountAutoFilled,
      accreditationStatus: accreditation.code,
      accreditationStatusName: accreditation.name,
      procedureReferral: referral.code,
      procedureReferralName: referral.name,
      treatingDoctorCode: treatingDoctor.code,
      treatingDoctor: treatingDoctor.name,
      procedureCode,
      procedureName: procParsed.procedureName,
      strats: procParsed.strats,
      procedureid: procParsed.procedureid ?? procParsed.id,
      stratOptionValue: item?.stratOptionValue,
      ...procParsed,
      ...(Object.keys(stratParsed).length ? stratParsed : {}),
    };
  });
};

/** Saved `treatmentPlansData` → form `items` for AyushmanProcedure. */
export const mapTreatmentPlansDataToFormItems = (
  treatmentPlansData: any[] = [],
  proceduresMaster: any[] = [],
  dateFormat = "DD-MM-YYYY HH:mm:ss",
): any[] => {
  return (treatmentPlansData || []).map((tp) => {
    const proc = tp?.procedure || {};
    const code =
      proc?.id ?? tp?.procedureCode ?? tp?.externalProcedureCode ?? "";
    const master = proceduresMaster?.find(
      (p) => String(p?.procedurecode) === String(code),
    );

    const procedureCodeJson = JSON.stringify({
      procedureCode: code,
      procedureName:
        proc?.name ?? tp?.procedureName ?? master?.procedurename ?? "",
      strats:
        tp?.strats ??
        master?.isstratificationapplicable ??
        proc?.strats,
      id: Number(
        tp?.procedureid ?? master?.procedureid ?? master?.id ?? proc?.id,
      ),
      implants: master?.implantsList || master?.implants || [],
      implantApplicable: master?.isimplantapplicable,
      specialityType: tp?.specialityType,
    });

    let stratOptionValue: string | undefined;
    if (tp?.stratOptionValue) {
      stratOptionValue =
        typeof tp.stratOptionValue === "string"
          ? tp.stratOptionValue
          : JSON.stringify(tp.stratOptionValue);
    } else if (
      tp?.stratification?.id ||
      tp?.stratification?.name ||
      tp?.stratificationCode
    ) {
      stratOptionValue = JSON.stringify({
        stratificationCode:
          tp?.stratification?.id ?? tp?.stratificationCode,
        stratificationName:
          tp?.stratification?.name ?? tp?.stratificationName,
      });
    } else if (tp?.stratificationCode || tp?.stratificationName) {
      stratOptionValue = JSON.stringify({
        stratificationCode: tp.stratificationCode,
        stratificationName: tp.stratificationName,
      });
    }

    const joinTilde = (code?: string, name?: string) =>
      code && name && code !== name ? `${code}~${name}` : code || name || "";

    return {
      procedureCode: procedureCodeJson,
      externalProcedureCode: tp?.externalProcedureCode ?? code,
      numberOfDays: String(
        tp?.numberOfDaysPerUnits ?? tp?.numberOfDays ?? "1",
      ),
      treatmentStartDate: tp?.treatmentStartDate
        ? dayjs(tp.treatmentStartDate, dateFormat)
        : null,
      procedureAmount: tp?.procedureAmount ?? proc?.procedureAmount,
      unspecifiedProcedureName:
        tp?.unspecifiedProcedureName ?? proc?.procedureName,
      unspecifiedProcedureAmount:
        tp?.unspecifiedProcedureAmount ?? proc?.procedureAmount,
      isAmountAutoFilled: tp?.isAmountAutoFilled,
      stratOptionValue,
      accreditationStatus: joinTilde(
        tp?.accreditationStatus,
        tp?.accreditationStatusName,
      ),
      procedureReferral: joinTilde(
        tp?.procedureReferral,
        tp?.procedureReferralName,
      ),
      treatingDoctor: joinTilde(tp?.treatingDoctorCode, tp?.treatingDoctor),
    };
  });
};
